import { NextRequest, NextResponse } from 'next/server'
import { and, asc, eq } from 'drizzle-orm'
import { requireExpertApiUser } from '@/lib/role-guard'
import { db } from '@/db'
import { users, tenantMemberships } from '@/db/schema'
import { sendAdminInviteEmail } from '@/lib/admin-invite-email'

const VALID_ROLES = ['line_manager', 'expert', 'admin'] as const
type UserRole = (typeof VALID_ROLES)[number]

export async function GET() {
  const auth = await requireExpertApiUser()
  if (!auth.ok) return auth.response

  const rows = await db
    .select({
      id: users.id,
      email: users.email,
      displayName: users.displayName,
      role: tenantMemberships.role,
      isActive: tenantMemberships.isActive,
      authProvider: users.authProvider,
      lastLoginAt: users.lastLoginAt,
      createdAt: users.createdAt,
    })
    .from(users)
    .innerJoin(
      tenantMemberships,
      eq(tenantMemberships.userId, users.id),
    )
    .where(eq(tenantMemberships.tenantId, auth.user.tenantId))
    .orderBy(asc(users.email))

  return NextResponse.json(rows)
}

export async function POST(request: NextRequest) {
  const auth = await requireExpertApiUser()
  if (!auth.ok) return auth.response

  const body = await request.json().catch(() => null)
  if (!body || typeof body.email !== 'string' || !body.email.trim()) {
    return NextResponse.json({ error: 'email is required' }, { status: 400 })
  }
  if (!VALID_ROLES.includes(body.role as UserRole)) {
    return NextResponse.json({ error: 'role must be line_manager, expert, or admin' }, { status: 400 })
  }

  const email = body.email.trim().toLowerCase()
  const role = body.role as UserRole

  // Check if user already exists
  const [existing] = await db
    .select({ id: users.id })
    .from(users)
    .where(eq(users.email, email))
    .limit(1)

  if (existing) {
    // Check for existing membership in this tenant
    const [existingMembership] = await db
      .select({ id: tenantMemberships.id, isActive: tenantMemberships.isActive })
      .from(tenantMemberships)
      .where(
        and(
          eq(tenantMemberships.userId, existing.id),
          eq(tenantMemberships.tenantId, auth.user.tenantId),
        ),
      )
      .limit(1)

    if (existingMembership) {
      if (existingMembership.isActive) {
        return NextResponse.json({ error: 'A user with that email already exists' }, { status: 409 })
      }
      // Re-activate a previously disabled membership and update role
      const [updated] = await db
        .update(tenantMemberships)
        .set({ role, isActive: true, updatedAt: new Date() })
        .where(eq(tenantMemberships.id, existingMembership.id))
        .returning()

      const [user] = await db.select().from(users).where(eq(users.id, existing.id)).limit(1)
      await sendAdminInviteEmail({ email, request })
      return NextResponse.json({ ...user, role: updated.role, isActive: updated.isActive }, { status: 200 })
    }

    // User exists but has no membership in this tenant — add one
    const [membership] = await db
      .insert(tenantMemberships)
      .values({ tenantId: auth.user.tenantId, userId: existing.id, role })
      .returning()

    const [user] = await db.select().from(users).where(eq(users.id, existing.id)).limit(1)
    await sendAdminInviteEmail({ email, request })
    return NextResponse.json({ ...user, role: membership.role, isActive: membership.isActive }, { status: 201 })
  }

  // New user — create users row then membership
  const [created] = await db
    .insert(users)
    .values({ email, authProvider: 'magic_link' })
    .returning()

  const [membership] = await db
    .insert(tenantMemberships)
    .values({ tenantId: auth.user.tenantId, userId: created.id, role })
    .returning()

  await sendAdminInviteEmail({ email, request })

  return NextResponse.json({ ...created, role: membership.role, isActive: membership.isActive }, { status: 201 })
}
